import * as ComponentLibrary from '@design-system/component-library';
import { customerMsg, t } from '../../../../common/i18n';
import { formatAddress } from '../../../../common/utils/accountUtils';
import { DeliveryPlace } from '../../placeOrderTypes';
import type { DeliveryAddress } from '../../../../generated/api/deliveryAddress';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { UseFormReturn } from 'react-hook-form';

const { Button, RadioGroup, RadioV2 } = ComponentLibrary;

interface AddressOptionProps {
  children: ReactNode;
  groupLabel?: string;
  id: string;
  label: string;
  name: string;
  onSelect: (place: DeliveryPlace) => void;
  selectedPlace?: DeliveryPlace;
  value: DeliveryPlace;
}

export const AddressOption = ({
  children,
  groupLabel,
  id,
  label,
  name,
  onSelect,
  selectedPlace,
  value,
}: AddressOptionProps) => (
  <div className="address-option-block">
    <RadioGroup
      className="address-option-group"
      label={groupLabel}
      name={name}
      onChange={selectedValue => selectedValue && onSelect(selectedValue as DeliveryPlace)}
      srOnlyLabel={Boolean(groupLabel)}
      value={selectedPlace}
    >
      <RadioV2 className="address-option" id={id} value={value} label={label} />
    </RadioGroup>
    <div className="address-option-extra">{children}</div>
  </div>
);

interface EditableAddressOption {
  actionLabel: string;
  editor: ReactNode;
  isEditing: boolean;
  onAction: () => void;
  summary?: string;
}

export const formatDeliveryAddress = (address: DeliveryAddress) =>
  `${formatAddress(address.address)}, ${address.companyName} | ${t.IY5V(customerMsg)}: ${address.recipient}, ${t.VG7P('Tel. ')}${address.phoneNumber}`;

interface EditableAddressOptionsParams {
  deliveryAddress?: DeliveryAddress;
  deliveryPlace?: DeliveryPlace;
  editingAddress: boolean;
  homeEditor: ReactNode;
  homeMethods: Pick<UseFormReturn<DeliveryAddress>, 'reset'>;
  onSelect: (place: DeliveryPlace) => void;
  otherEditor: ReactNode;
  otherMethods: Pick<UseFormReturn<DeliveryAddress>, 'reset'>;
  setEditingAddress: Dispatch<SetStateAction<boolean>>;
}

export const createEditableAddressOptions = ({
  deliveryAddress,
  deliveryPlace,
  editingAddress,
  homeEditor,
  homeMethods,
  onSelect,
  otherEditor,
  otherMethods,
  setEditingAddress,
}: EditableAddressOptionsParams) => {
  const createOption = (
    place: DeliveryPlace,
    editor: ReactNode,
    methods: Pick<UseFormReturn<DeliveryAddress>, 'reset'>
  ): EditableAddressOption => {
    const hasSelectedAddress = Boolean(deliveryAddress && deliveryPlace === place);

    return {
      actionLabel: hasSelectedAddress ? t.NVPK('Edit') : t.JP9Z('Add address information'),
      editor,
      isEditing: editingAddress && deliveryPlace === place,
      onAction: () => {
        if (deliveryAddress && deliveryPlace === place) {
          methods.reset(deliveryAddress);
          setEditingAddress(true);
        } else {
          onSelect(place);
        }
      },
      summary: hasSelectedAddress && deliveryAddress ? formatDeliveryAddress(deliveryAddress) : undefined,
    };
  };

  return {
    home: createOption(DeliveryPlace.HOME_ADDRESS, homeEditor, homeMethods),
    other: createOption(DeliveryPlace.OTHER_ADDRESS, otherEditor, otherMethods),
  };
};

const AddressOptionContent = ({ actionLabel, editor, isEditing, onAction, summary }: EditableAddressOption) =>
  isEditing ? (
    editor
  ) : (
    <>
      {summary && <div>{summary}</div>}
      <Button
        onClick={event => {
          event.preventDefault();
          onAction();
        }}
        color="tertiary"
        size="m"
      >
        {actionLabel}
      </Button>
    </>
  );

interface AddressSelectionOptionsProps {
  companySummary?: string;
  home: EditableAddressOption;
  onSelect: (place: DeliveryPlace) => void;
  other: EditableAddressOption;
  selectedPlace?: DeliveryPlace;
}

export const AddressSelectionOptions = ({
  companySummary,
  home,
  onSelect,
  other,
  selectedPlace,
}: AddressSelectionOptionsProps) => (
  <div className="of-place-order__address-selection__radio-container">
    {companySummary && (
      <AddressOption
        groupLabel={t.AK55('Select delivery address')}
        id="company-address"
        label={t.B7P0(`Delivery to the company's headquarters`)}
        name="deliveryMethod-company"
        onSelect={onSelect}
        selectedPlace={selectedPlace}
        value={DeliveryPlace.COMPANY_ADDRESS}
      >
        {companySummary}
      </AddressOption>
    )}

    <AddressOption
      groupLabel={companySummary ? undefined : t.AK55('Select delivery address')}
      id="other-address"
      label={t.FG7T('Delivery to another company location')}
      name="deliveryMethod-other"
      onSelect={onSelect}
      selectedPlace={selectedPlace}
      value={DeliveryPlace.OTHER_ADDRESS}
    >
      <AddressOptionContent {...other} />
    </AddressOption>

    <AddressOption
      id="home-address"
      label={t.D4FU('Delivery to home address')}
      name="deliveryMethod-home"
      onSelect={onSelect}
      selectedPlace={selectedPlace}
      value={DeliveryPlace.HOME_ADDRESS}
    >
      <AddressOptionContent {...home} />
    </AddressOption>
  </div>
);
